'use client';

import { useState, useRef, useEffect } from 'react';
import { ErrorMessage } from './ErrorMessage';
import { InfoIcon } from 'mode-shared/components/icons';
import { Tooltip } from 'mode-shared/components';
import { twMerge } from 'tailwind-merge';

export interface DropdownOption<T> {
  value: T;
  label: string;
  disabled?: boolean;
  desc?: string;
}

export function Dropdown<T extends string | number>({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  className = '',
  label,
  error,
  helperText,
}: {
  options: DropdownOption<T>[];
  value: T;
  onChange: (value: T) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  error?: string;
  helperText?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (optionValue: T) => {
    if (!disabled) {
      onChange(optionValue);
      setIsOpen(false);
    }
  };

  return (
    <div className={`relative w-full ${className}`}>
      {label && (
        <label htmlFor={label} className="flex items-center gap-1 text-sm font-medium text-neutral-300 mb-2">
          {label}
          {helperText && <Tooltip trigger={<InfoIcon className="text-neutral-500" />}>{helperText}</Tooltip>}
        </label>
      )}
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            relative w-full px-3 py-2 text-left bg-neutral-1000 border border-neutral-900 rounded-lg
            focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            ${isOpen ? 'ring-2 ring-primary border-primary' : ''}
          `}
        >
          <span className={selectedOption ? 'text-neutral-300' : 'text-neutral-500'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg
              className={`w-4 h-4 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </button>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-neutral-1000 border border-neutral-600 rounded-lg shadow-lg max-h-60 overflow-auto max-h-[182px]">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleOptionClick(option.value)}
                disabled={option.disabled}
                className={`
                  w-full px-3 py-2 text-left text-sm flex items-center justify-between
                  ${
                    option.disabled
                      ? 'text-neutral-500 cursor-not-allowed'
                      : 'text-neutral-100 hover:bg-neutral-800 hover:text-white cursor-pointer'
                  }
                  ${option.value === value ? 'bg-primary text-neutral-black pointer-events-none font-bold' : ''}
                `}
              >
                {option.label}
                {option.desc && (
                  <span
                    className={twMerge(
                      option.disabled ? 'text-xs text-neutral-500' : 'text-xs text-neutral-300',
                      option.value === value ? 'text-neutral-black font-bold' : '',
                      'text-xs'
                    )}
                  >
                    {option.desc}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>
    </div>
  );
}
